import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { ButtonLink } from '@/components/landing/button-link'
import { CourtFloor } from '@/components/landing/court-floor'
import { KeyedImage } from '@/components/landing/keyed-image'
import { setBallDirector } from '@/lib/ball-director'
import { addTicker } from '@/lib/ticker'

import {
    easeInCubic,
    easeInOutCubic,
    easeOutCubic,
    lerp,
    prefersReducedMotion,
    progressBetween,
    stickyProgress
} from '@/lib/motion'

import { EVENT } from '@/constants/event'

/**
 * Four layers (court, disc, the word, the player) that tilt apart in 3D as you scroll.
 * The 3D ball starts above the player's pointing hand, then rushes the camera and fills the screen,
 * which hands over to the lime section below.
 */
export const HeroSection: FC = (): ReactNode => {
    const trackRef = useRef<HTMLElement>(null)
    const sceneRef = useRef<HTMLDivElement>(null)
    const courtRef = useRef<HTMLDivElement>(null)
    const discRef = useRef<HTMLDivElement>(null)
    const wordRef = useRef<HTMLDivElement>(null)
    const playerRef = useRef<HTMLDivElement>(null)
    const anchorRef = useRef<HTMLDivElement>(null)
    const infoRef = useRef<HTMLDivElement>(null)
    const veilRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const reduced = prefersReducedMotion()
        const state = { visible: true, tilt: 0, zoom: 0, fade: 0 }

        const layers: Array<[HTMLDivElement | null, number]> = [
            [courtRef.current, -320],
            [discRef.current, -190],
            [wordRef.current, -40],
            [playerRef.current, 160]
        ]

        const removeTicker = addTicker(() => {
            const track = trackRef.current
            if (!track) return

            const rect = track.getBoundingClientRect()
            const viewportHeight = window.innerHeight

            state.visible = rect.bottom > 0 && rect.top < viewportHeight
            if (!state.visible) return

            const progress = stickyProgress(rect, viewportHeight)

            state.tilt = reduced ? 0 : easeInOutCubic(progressBetween(progress, 0, 0.4))
            state.zoom = reduced ? 0 : progressBetween(progress, 0.4, 0.82)
            state.fade = reduced ? 0 : progressBetween(progress, 0.8, 0.97)

            const tilt = state.tilt
            const zoom = easeInCubic(state.zoom)

            if (sceneRef.current) {
                sceneRef.current.style.transform = `translate3d(0, ${-3 * tilt}vh, 0) scale(${1 - 0.1 * tilt - 0.1 * zoom}) rotateX(${14 * tilt}deg) rotateY(${-26 * tilt}deg) rotateZ(${2 * tilt}deg)`
            }

            for (const [layer, depth] of layers) {
                if (!layer) continue
                layer.style.transform = `translate3d(0, 0, ${depth * tilt}px)`
                layer.style.opacity = String(1 - 0.92 * zoom)
            }

            if (infoRef.current) {
                const leave = progressBetween(progress, 0, 0.16)
                infoRef.current.style.opacity = String(1 - leave)
                infoRef.current.style.transform = `translate3d(0, ${leave * 36}px, 0)`
                infoRef.current.style.visibility = leave >= 1 ? 'hidden' : 'visible'
            }

            if (veilRef.current) veilRef.current.style.opacity = String(state.fade)
        }, 0)

        const removeDirector = setBallDirector('hero', 1, ({ width, height, time }) => {
            const anchor = anchorRef.current
            if (!state.visible || !anchor) return null

            const rect = anchor.getBoundingClientRect()
            if (!rect.width) return null

            const radius = Math.max(rect.width, rect.height) / 2
            const anchorX = rect.left + rect.width / 2
            const anchorY = rect.top + rect.height / 2
            const bob = reduced ? 0 : Math.sin(time * 1.8) * radius * 0.32 * (1 - state.zoom)
            const travel = easeInOutCubic(state.zoom)
            const grow = easeInCubic(state.zoom)

            return {
                x: lerp(anchorX, width / 2, travel),
                y: lerp(anchorY + bob, height / 2, travel),
                radius: lerp(radius, Math.hypot(width, height) * 0.62, grow),
                opacity: 1 - easeOutCubic(state.fade),
                spin: reduced ? 0.25 : 1 + state.zoom * 5
            }
        })

        return () => {
            removeTicker()
            removeDirector()
        }
    }, [])

    return (
        <section
            id='hero'
            ref={trackRef}
            aria-labelledby='hero-title'
            className='relative h-[260vh] bg-court-deep motion-reduce:h-svh'
        >
            <div className='sticky top-0 h-svh overflow-hidden'>
                <div aria-hidden='true' className='absolute inset-0 bg-[radial-gradient(120%_85%_at_50%_26%,#3b61e8_0%,#2544c8_46%,#172b8c_100%)]' />
                <div aria-hidden='true' className='grain-overlay absolute inset-0' />

                <div className='absolute inset-0 [perspective:1400px] [perspective-origin:50%_45%]'>
                    <div ref={sceneRef} className='absolute inset-0 [transform-style:preserve-3d] will-change-transform'>
                        <div ref={courtRef} aria-hidden='true' className='absolute inset-x-0 bottom-0 h-[44%] text-cream/20'>
                            <CourtFloor />
                        </div>

                        <div ref={discRef} aria-hidden='true' className='absolute inset-0'>
                            <div className='absolute left-1/2 top-[44%] aspect-square w-[min(88vw,58svh)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle_at_34%_28%,#3156dc_0%,#1c3299_58%,#142577_100%)] shadow-[0_40px_120px_-30px_rgba(8,12,40,0.7)] md:left-[53%] md:top-[48%] md:w-[min(46vw,76svh)]' />
                        </div>

                        <div ref={wordRef} className='absolute inset-0 flex items-start justify-center pt-[21svh] md:items-center md:pt-0'>
                            <h1 id='hero-title' className='flex flex-col items-start md:-mt-[8svh]'>
                                <span className='serif-soft relative z-10 -mb-[0.3em] ml-[0.18em] -rotate-6 font-serif text-[length:min(15vw,10svh)] font-normal italic leading-none text-lime md:text-[length:min(8.4vw,15svh)]'>
                                    Kitchen
                                </span>

                                <span className='font-heading text-[length:min(42vw,34svh)] font-black uppercase leading-[0.8] tracking-[-0.01em] text-cream md:text-[length:min(36vw,56svh)]'>
                                    Open
                                </span>

                                <span className='sr-only'>
                                    {`, a pickleball tournament, ${EVENT.dates}, ${EVENT.city}`}
                                </span>
                            </h1>
                        </div>

                        <div ref={playerRef} className='absolute inset-0'>
                            <div className='absolute bottom-[10%] left-1/2 aspect-[1792/2400] h-[72%] -translate-x-1/2 md:bottom-[-3.5%] md:left-[53%] md:h-[93%]'>
                                <KeyedImage
                                    src='/images/hero-player.jpg'
                                    width={1792}
                                    height={2400}
                                    alt='A grandmother in a visor and coral top leaps for an overhead smash, paddle cocked, pointing at the ball'
                                />

                                <div
                                    ref={anchorRef}
                                    aria-hidden='true'
                                    className='absolute left-[66%] top-[5%] aspect-square h-[6.6%] -translate-x-1/2 -translate-y-1/2'
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 bottom-0 h-[34%] bg-linear-to-t from-court-deep/90 via-court-deep/40 to-transparent md:h-[26%]' />

                <div ref={infoRef} className='absolute inset-x-0 bottom-0 z-10'>
                    <div className='mx-auto flex max-w-[1440px] items-end justify-between gap-6 px-5 pb-6 md:px-10 md:pb-10'>
                        <div className='max-w-md text-cream'>
                            <p className='font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-lime'>
                                {EVENT.tagline}
                            </p>

                            <p className='mt-2 font-heading text-[2.6rem] font-extrabold uppercase leading-[0.9] md:text-6xl'>
                                {EVENT.datesCompact}
                            </p>

                            <p className='mt-2 text-sm text-cream/80'>
                                {EVENT.city}
                                <span aria-hidden='true' className='px-2 text-cream/40'>·</span>
                                560 players
                                <span aria-hidden='true' className='px-2 text-cream/40'>·</span>
                                18 courts
                            </p>

                            <div className='mt-6 hidden flex-wrap gap-3 [@media(min-width:768px)_and_(min-height:761px)]:flex'>
                                <ButtonLink href='#tickets'>
                                    Get passes
                                </ButtonLink>

                                <ButtonLink href='#play' variant='outline-cream'>
                                    Enter a team
                                </ButtonLink>
                            </div>
                        </div>

                        <a
                            href='#numbers'
                            className='hidden items-center gap-4 rounded-full font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-cream/80 transition-colors hover:text-lime focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lime md:flex'
                        >
                            Scroll to serve
                            <span aria-hidden='true' className='relative block h-12 w-px overflow-hidden bg-cream/25'>
                                <span className='absolute inset-x-0 top-0 block h-1/2 animate-scroll-cue bg-lime motion-reduce:animate-none' />
                            </span>
                        </a>
                    </div>
                </div>

                <div ref={veilRef} aria-hidden='true' className='pointer-events-none absolute inset-0 z-20 bg-lime opacity-0' />
            </div>
        </section>
    )
}
